import {
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { Button, Grid, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { theme } from "../../../theme/theme";
import type { TimeSeriesFilters as TimeSeriesFiltersType } from "../../../types";
import type { DateRangeValue } from "./date-range-filter";
import { TimeSeriesChart } from "./time-series-chart";
import { TimeSeriesFilters } from "./time-series-filters";

interface TimeSeriesTabProps {
  dateRange: DateRangeValue;
}

export function TimeSeriesTab({ dateRange }: TimeSeriesTabProps) {
  const [filters, setFilters] = useState<TimeSeriesFiltersType>({
    groupByAppConfig: false,
  });
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const handleRefreshAll = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  return (
    <Stack spacing={3}>
      {/* Tab-level filters with Refresh All button */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <TimeSeriesFilters value={filters} onChange={setFilters} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshAll}
        >
          Refresh All
        </Button>
      </Stack>

      {/* Charts Grid - 2x2 layout */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Tests Created Over Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TimeSeriesChart
            title="Tests Created Over Time"
            subtitle="Cumulative count of tests created"
            endpoint="/analytics/timeseries/tests-created"
            chartType="bar"
            defaultGranularity="week"
            filters={filters}
            dateRange={dateRange}
            color={theme.palette.primary.main}
            icon={<TrendingUpIcon color="primary" fontSize="small" />}
            refetchTrigger={refetchTrigger}
          />
        </Grid>

        {/* Test Runs Over Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TimeSeriesChart
            title="Test Runs Over Time"
            subtitle="Cumulative count of test executions"
            endpoint="/analytics/timeseries/test-runs"
            chartType="bar"
            defaultGranularity="week"
            filters={filters}
            dateRange={dateRange}
            color={theme.palette.info.main}
            icon={<SpeedIcon color="info" fontSize="small" />}
            refetchTrigger={refetchTrigger}
          />
        </Grid>

        {/* Pass Rate Over Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TimeSeriesChart
            title="Pass Rate Over Time"
            subtitle="Percentage of tests passing per period. Target is above 95%."
            endpoint="/analytics/timeseries/pass-rate"
            chartType="bar"
            defaultGranularity="week"
            filters={filters}
            dateRange={dateRange}
            valueFormatter={(v) => (v != null ? `${v.toFixed(1)}%` : "-")}
            color={theme.palette.success.main}
            icon={<CheckCircleIcon color="success" fontSize="small" />}
            referenceLine={{
              y: 95,
              label: "🏆",
              tooltip: "Target Pass Rate",
              color: theme.palette.common.black,
            }}
            refetchTrigger={refetchTrigger}
          />
        </Grid>

        {/* Average PFS Over Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TimeSeriesChart
            title="Average PFS Score Over Time"
            subtitle="Average flakiness score of tests executed per period (lower is better). Target is below 5%."
            endpoint="/analytics/timeseries/avg-pfs"
            chartType="bar"
            defaultGranularity="month"
            filters={filters}
            dateRange={dateRange}
            valueFormatter={(v) => (v != null ? `${(v * 100).toFixed(1)}%` : "-")}
            color={theme.palette.warning.main}
            icon={<BugReportIcon color="warning" fontSize="small" />}
            referenceLine={{
              y: 0.05,
              label: "⚠️",
              tooltip: "Target PFS Score",
              color: theme.palette.common.black,
            }}
            refetchTrigger={refetchTrigger}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
